// ============================================================
// CORRELATION ENGINE — Fase 10.13
// Correlaciona eventos de visão, supply, clima, SST, produção
// Detecta riscos compostos e amplificação de risco
// ============================================================
import type { EventCorrelation, SpatialRisk, Equipment } from '@/types/twin'

export interface CorrelationContext {
  obraId:       string
  spatialRisks: SpatialRisk[]
  equipment:    Equipment[]
  correlations: EventCorrelation[]  // existing correlations to avoid dups
}

// ─── Correlation rules ───────────────────────────────────────
type CorrelationRule = {
  id:          string
  name:        string
  description: string
  detect:      (ctx: CorrelationContext) => EventCorrelation | null
}

const CORRELATION_RULES: CorrelationRule[] = [
  {
    id:   'supply_rain_workforce',
    name: 'Supply + Chuva + Efetivo Baixo',
    description: 'Tríade de risco: supply crítico + clima desfavorável + efetivo reduzido',
    detect: (ctx) => {
      const hasSupply   = ctx.spatialRisks.some(r => r.risk_type === 'supply' && r.score > 0.5)
      const hasWeather  = ctx.spatialRisks.some(r => r.risk_type === 'weather' && r.score > 0.5)
      const hasProduction = ctx.spatialRisks.some(r => r.risk_type === 'production' && r.score > 0.4)
      if (!hasSupply || !hasWeather || !hasProduction) return null

      return buildCorrelation(ctx.obraId, ['supply', 'weather', 'production'],
        'compound_risk', 'critical',
        '⚠ Supply + Chuva + Efetivo baixo → Alto risco concretagem',
        'A combinação de supply crítico, clima desfavorável e efetivo reduzido cria risco composto para a concretagem programada.',
        2.1,
      )
    },
  },
  {
    id:   'equipment_production_delay',
    name: 'Equipamento Offline + Produção Atrasada',
    description: 'Equipamento crítico offline causando atraso de produção',
    detect: (ctx) => {
      const criticalOffline = ctx.equipment.some(e =>
        (e.status === 'offline' || e.status === 'critical') && e.criticality === 'critical'
      )
      const productionRisk = ctx.spatialRisks.some(r => r.risk_type === 'production' && r.score > 0.55)
      if (!criticalOffline || !productionRisk) return null

      return buildCorrelation(ctx.obraId, ['equipment_failure', 'production'],
        'causal', 'critical',
        '⚠ Equipamento crítico offline → Produção comprometida',
        'Falha em equipamento crítico está impactando diretamente a produção da obra.',
        1.8,
      )
    },
  },
  {
    id:   'sst_logistics_blocked',
    name: 'SST + Logística Bloqueada',
    description: 'Incidente SST agravado por rota de emergência comprometida',
    detect: (ctx) => {
      const sstCritical   = ctx.spatialRisks.some(r => r.risk_type === 'sst' && r.severity === 'critical')
      const logisticsHigh = ctx.spatialRisks.some(r => r.risk_type === 'logistics' && r.score > 0.6)
      if (!sstCritical || !logisticsHigh) return null

      return buildCorrelation(ctx.obraId, ['sst', 'logistics'],
        'compound_risk', 'emergency',
        '🚨 SST crítico + Logística comprometida → Evacuação em risco',
        'Risco crítico de SST combinado com bloqueio logístico pode comprometer rotas de evacuação.',
        2.5,
      )
    },
  },
  {
    id:   'multi_zone_sst_spread',
    name: 'SST em Múltiplas Zonas',
    description: 'Risco SST se propagando por múltiplas zonas',
    detect: (ctx) => {
      const sstZones = new Set(
        ctx.spatialRisks
          .filter(r => r.risk_type === 'sst' && r.is_active)
          .map(r => r.zone_id)
          .filter(Boolean),
      )
      if (sstZones.size < 2) return null

      return buildCorrelation(ctx.obraId, Array.from(sstZones) as string[],
        'spatial', 'critical',
        `⚠ Risco SST em ${sstZones.size} zonas simultâneas`,
        `Risco de segurança ativo em ${sstZones.size} áreas diferentes da obra ao mesmo tempo.`,
        1.6,
      )
    },
  },
]

// ─── Run all correlation rules ────────────────────────────────
export function detectCorrelations(ctx: CorrelationContext): EventCorrelation[] {
  const existing = new Set(ctx.correlations.map(c => c.title))
  const detected: EventCorrelation[] = []

  for (const rule of CORRELATION_RULES) {
    try {
      const corr = rule.detect(ctx)
      if (!corr) continue
      if (existing.has(corr.title)) continue   // dedup by title
      detected.push(corr)
    } catch {/* ignore individual rule failures */}
  }

  return detected
}

// ─── Factory ────────────────────────────────────────────────
let _seq = 0

function buildCorrelation(
  obraId:           string,
  eventTypes:       string[],
  correlationType:  EventCorrelation['correlation_type'],
  severity:         EventCorrelation['severity'],
  title:            string,
  description:      string,
  riskAmplifier:    number,
): EventCorrelation {
  return {
    id:               `corr_${Date.now()}_${++_seq}`,
    obra_id:          obraId,
    event_ids:        [],
    event_types:      eventTypes,
    correlation_type: correlationType,
    severity,
    title,
    description,
    risk_amplifier:   riskAmplifier,
    is_active:        true,
    created_at:       new Date().toISOString(),
  }
}
